import { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Send, Image, Loader2, Plus, X, Music } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GifPicker, type GifData } from './gif-picker';
import { TrackPicker, type TrackData } from './track-picker';
import { LeaguePicker, type LeagueData } from './league-picker';
// Note: Keyboard accessory bar hiding is done via native iOS code in AppDelegate.swift

export interface SceneContext {
  channelId: number;
  channelName: string;
  categoryId: number | null;
  categoryType: string | null;
}

/** @deprecated Use SceneContext instead */
export type CommunityContext = SceneContext;

interface ChatInputProps {
  onSendMessage: (content: string) => Promise<void>;
  onSendGif?: (gifData: GifData, comment?: string) => Promise<void>;
  onSendTrack?: (trackData: TrackData, comment?: string) => Promise<void>;
  onSendLeague?: (leagueData: LeagueData, comment?: string) => Promise<void>;
  isSending: boolean;
  disabled?: boolean;
  disabledReason?: string;
  imagesAllowed?: boolean;
  onImageUpload?: (file: File) => Promise<void>;
  leagueId?: number;
  communityContext?: SceneContext;
}

export function ChatInput({
  onSendMessage,
  onSendGif,
  onSendTrack,
  onSendLeague,
  isSending,
  disabled = false,
  disabledReason,
  imagesAllowed = false,
  onImageUpload,
  leagueId,
  communityContext,
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [showAttachments, setShowAttachments] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || isSending || disabled) return;

    // Clear message immediately for better UX
    const messageToSend = trimmed;
    setMessage('');
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      await onSendMessage(messageToSend);
    } catch (error) {
      // Restore message on error so user doesn't lose their text
      setMessage(messageToSend);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
    setShowAttachments(false);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('📷 handleFileChange triggered');
    const file = e.target.files?.[0];
    console.log('📷 File selected:', file?.name, file?.type, file?.size);
    console.log('📷 onImageUpload exists:', !!onImageUpload);
    if (file && onImageUpload) {
      console.log('📷 Calling onImageUpload...');
      try {
        await onImageUpload(file);
        console.log('📷 onImageUpload completed');
      } catch (err) {
        console.error('📷 onImageUpload error:', err);
      }
    } else {
      console.log('📷 Skipping upload - file:', !!file, 'onImageUpload:', !!onImageUpload);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleGifSelect = async (gifData: GifData) => {
    if (onSendGif) {
      setShowAttachments(false);
      // Use current message as comment if there's text
      const comment = message.trim() || undefined;
      setMessage('');
      await onSendGif(gifData, comment);
    }
  };

  const handleTrackSelect = async (trackData: TrackData) => {
    if (onSendTrack) {
      setShowAttachments(false);
      // Use current message as comment if there's text
      const comment = message.trim() || undefined;
      setMessage('');
      await onSendTrack(trackData, comment);
    }
  };

  const handleLeagueSelect = async (leagueData: LeagueData) => {
    if (onSendLeague) {
      setShowAttachments(false);
      const comment = message.trim() || undefined;
      setMessage('');
      await onSendLeague(leagueData, comment);
    }
  };

  // Auto-resize textarea
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    // Auto-resize
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  const isDisabled = disabled || isSending;
  const canSend = message.trim().length > 0 && !isDisabled;

  // GIFs are always allowed even if images are disabled
  const hasAttachmentOptions = imagesAllowed || onSendGif || onSendTrack || onSendLeague;

  return (
    <div className={cn("border-t bg-background px-3 pt-3", communityContext && !showAttachments ? "pb-0" : "pb-3")}>
      {/* Disabled message */}
      {disabled && disabledReason && (
        <div className="mb-2 px-3 py-2 bg-destructive/10 text-destructive rounded-lg text-sm text-center">
          {disabledReason}
        </div>
      )}

      {/* Character count warning */}
      {message.length > 450 && (
        <div className="mb-2 px-3 py-1 text-xs text-muted-foreground text-right">
          {message.length}/500 characters
        </div>
      )}

      {/* Attachment bar - expanded view */}
      {showAttachments && (
        <div className="mb-2 flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setShowAttachments(false)}
          >
            <X className="h-4 w-4" />
          </Button>

          {/* Image upload button */}
          {imagesAllowed && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={handleImageClick}
              disabled={isDisabled}
            >
              <Image className="h-4 w-4" />
              Photo
            </Button>
          )}

          {/* GIF picker - always available */}
          {onSendGif && (
            <GifPicker
              onSelect={handleGifSelect}
              disabled={isDisabled}
            />
          )}

          {/* Track picker */}
          {onSendTrack && leagueId && (
            <TrackPicker
              leagueId={leagueId}
              onSelect={handleTrackSelect}
              disabled={isDisabled}
            />
          )}

          {/* League picker */}
          {onSendLeague && (
            <LeaguePicker
              onSelect={handleLeagueSelect}
              disabled={isDisabled}
              communityContext={communityContext}
            />
          )}
        </div>
      )}

      {/* Hidden file input - MUST be outside the showAttachments conditional
          so it stays in DOM when file picker dialog is open */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="flex items-end gap-2">
        {/* Attachment toggle button */}
        {hasAttachmentOptions && !showAttachments && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="flex-shrink-0 h-10 w-10"
            onClick={() => setShowAttachments(true)}
            disabled={isDisabled}
          >
            <Plus className="h-5 w-5" />
          </Button>
        )}

        {/* Message input */}
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          enterKeyHint="send"
          onFocus={(e) => {
            // Scroll input into view when keyboard appears on mobile
            // Use 'nearest' to avoid jerking page content when keyboard opens
            setTimeout(() => {
              e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 300);
          }}
          placeholder={disabled ? disabledReason || 'Chat disabled' : showAttachments ? 'Add a comment (optional)...' : 'Send a message...'}
          disabled={isDisabled}
          maxLength={500}
          rows={1}
          className={cn(
            'flex-1 min-h-[40px] max-h-[120px] resize-none py-2.5 px-4 rounded-2xl',
            'focus-visible:ring-1 focus-visible:ring-offset-0'
          )}
        />

        {/* Send button */}
        <Button
          type="button"
          size="icon"
          className="flex-shrink-0 h-10 w-10 rounded-full"
          onClick={handleSend}
          disabled={!canSend}
        >
          {isSending ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </Button>
      </div>

      {communityContext && !showAttachments && (
        <p className="text-[11px] text-muted-foreground/70 text-center px-4 mt-0.5 mb-0 leading-none">
          Tap + to share music, leagues, or GIFs
        </p>
      )}
    </div>
  );
}
